import { useRef } from 'react';
import {
  Box,
  Container,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, useInView } from 'framer-motion';
import { P} from '../theme/palette';
import { FEATURESHIGHLIGHTS } from '../data/featuresHighligth';
import { useTenantConfig } from '../hooks/useTenant';

const BentoCard = styled(motion.div)({
  position: 'relative',
  borderRadius: 24,
  padding: '28px 24px',
  height: '100%',
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  cursor: 'default',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px) scale(1.01)',
    '& .bento-icon': {
      transform: 'scale(1.1) rotate(-3deg)',
    },
    '& .bento-glow': {
      opacity: 1,
    },
  },
});


/* Section badge (pill) - Kurumsal Blue */
const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  borderRadius: 50,
  background: 'rgba(30, 64, 175, 0.06)',
  border: '1px solid rgba(30, 64, 175, 0.12)',
  marginBottom: 24,
});

const containerV = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.5, staggerChildren: 0.12 },
  },
};


const FeatureHighlightsKurumsalNew = () => {
  const { colors } = useTenantConfig();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-60px' });

  return (
    <Box
      ref={sectionRef}
      sx={{
        position: 'relative',
        py: { xs: 8, md: 15 },
        background: 'linear-gradient(165deg, rgba(239, 246, 255, 0.4) 0%, rgba(236, 254, 255, 0.3) 40%, #ffffff 70%)',
        overflow: 'hidden',
        borderBottom: '1px solid #e5e7eb',
      }}
    >
      {/* ── Subtle background pattern ── */}
      <Box sx={{
        position: 'absolute', inset: 0, opacity: 0.4,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${P.neutral[200]} 1px, transparent 0)`,
        backgroundSize: '48px 48px',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          variants={containerV}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* ════════════════════════════════════
              SECTION HEADER
              ════════════════════════════════════ */}
          <motion.div>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
              {/* Badge */}
              <SectionBadge>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  }}
                />
                <Typography sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.5px',
                }}>
                  DERNEKSİSTEM PLATFORMU
                </Typography>
              </SectionBadge>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.4rem', md: '3rem' },
                  color: '#0f172a',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  mb: 2.5,
                }}
              >
                Dernek Yönetiminde {' '}
                <br />
                <Box component="span" sx={{
                  color: '#0f172a',
                }}>
                  Yeni Bir Standart
                </Box>
              </Typography>
              <Typography
                sx={{
                  maxWidth: 520,
                  mx: 'auto',
                  color: P.neutral[500],
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  lineHeight: 1.7,
                }}
              >
                Dinamik yönetim platformu ile derneğinizin tüm dijital ihtiyaçlarını tek platformda çözün.
              </Typography>
            </Box>
          </motion.div>

          {/* ════════════════════════════════════
              EQUAL SIZE GRID
              ════════════════════════════════════ */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
              gap: { xs: 2.5, md: 3 },
            }}
          >
            {FEATURESHIGHLIGHTS.map((feat) => (
              <motion.div
                key={feat.id}
                // variants={itemV}
                style={{ display: 'flex' }}
              >
                <BentoCard
                  sx={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #ffffff 0%, rgba(239, 246, 255, 0.6) 100%)',
                    border: '1.5px solid rgba(30, 64, 175, 0.08)',
                    boxShadow: '0 4px 24px rgba(30, 64, 175, 0.04)',
                    minHeight: 220,
                    '&:hover': {
                      boxShadow: '0 12px 40px rgba(30, 64, 175, 0.12), 0 0 0 1.5px rgba(30, 64, 175, 0.15)',
                      borderColor: 'rgba(30, 64, 175, 0.2)',
                      background: 'linear-gradient(135deg, #ffffff 0%, rgba(239, 246, 255, 0.9) 100%)',
                    },
                  }}
                >
                  {/* Glow effect on hover */}
                  <Box
                    className="bento-glow"
                    sx={{
                      position: 'absolute',
                      top: -50, right: -50,
                      width: 200, height: 200,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(30, 64, 175, 0.08) 0%, transparent 70%)',
                      opacity: 0,
                      transition: 'opacity 0.5s ease',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Content */}
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1, }}>
                    {/* Icon */}
                    <Box
                      className="bento-icon"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 65,
                        height: 65,
                        borderRadius: 14,
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(239, 246, 255, 0.8) 100%)',
                        boxShadow: '0 4px 16px rgba(30, 64, 175, 0.12)',
                        border: '1px solid rgba(30, 64, 175, 0.15)',
                        mb: 2.5,
                        transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
                      }}
                    >
                      <img src={feat.icon} alt={`${feat.title} icon`} style={{ width: '55%', height: '55%' }} />
                    </Box>

                    {/* Title */}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        color: '#0f172a',
                        mb: 1,
                        lineHeight: 1.3,
                      }}
                    >
                      {feat.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      sx={{
                        fontSize: '0.88rem',
                        color: '#475569',
                        lineHeight: 1.65,
                      }}
                    >
                      {feat.desc}
                    </Typography>
                  </Box>

                  {/* Glow on hover */}
                  <Box
                    className="bento-glow"
                    sx={{
                      position: 'absolute',
                      top: -30, right: -30,
                      width: 100, height: 100,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(8, 145, 178, 0.1) 0%, transparent 70%)',
                      opacity: 0,
                      transition: 'opacity 0.5s ease',
                      pointerEvents: 'none',
                    }}
                  />
                </BentoCard>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FeatureHighlightsKurumsalNew;
